import { useEffect, useState } from "react"
import FormErrors from "components/formParts/formErrors"
import Callout from "components/callout/callout"
import { CustomInput, CustomInputGroups, CustomInputAttributes, CustomInputWrite } from "types"
import { useTranslation } from "react-i18next"
import { DragDropContext, Droppable, DragStart, DropResult } from '@hello-pangea/dnd'
import EditCustomInputsFormGroup from "./editCustomInputsFormGroup"
import useLayout from "hooks/useLayout"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Button from "components/inputs/button"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import { logger } from "utils/secureLogger"
import Form from "components/forms/form"
import useCategories from "hooks/useCategories"

interface EditInputsFormProps {
    storageId: string
    categoryId: string
}

const EditCustomInputsForm: React.FC<EditInputsFormProps> = ({ storageId, categoryId }) => {

    const { t } = useTranslation()

    const { allCategories, categoriesStatus, setCustomInputsAsync, isSettingCustomInputs } = useCategories({ storageId })

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    const [groups, setGroups] = useState<CustomInputGroups>({})
    const [customInputGroupOrder, setCustomInputGroupOrder] = useState<string[]>([])

    const [isDraggingGroup, setIsDraggingGroup] = useState(false)
    const [isDraggingField, setIsDraggingField] = useState(false)

    const {
        openDialog,
        addToast
    } = useLayout()

    // categories are loaded via hook

    // Catch custom fields.
    // This is only for simplicity.
    useEffect(() => {
        let isMounted = true;
        
        if (!isMounted) return;
        const category = allCategories.find(c => c.id === categoryId)
        if (category?.customInputs?.customInputGroups) {
            setGroups(category.customInputs.customInputGroups as CustomInputGroups)
        }
        if (category?.customInputs?.customInputGroupOrder) {
            setCustomInputGroupOrder(category.customInputs.customInputGroupOrder as string[])
        }
        
        return () => {
            isMounted = false;
        };
    }, [allCategories, storageId, categoryId])

    // Handle drag start.
    // Used for determining if the user is dragging a group or a field.
    // This is used for conditional rendering of the drop area.
    const handleDragStart = (result: DragStart): void => {
        if (result.type === `group`) {
            setIsDraggingGroup(true)
        } else if (result.type === `field`) {
            setIsDraggingField(true)
        }
    }

    // Handle drag end.
    // Used for reordering groups and fields.
    const handleDragEnd = (result: DropResult): void => {
        
        setIsDraggingGroup(false)
        setIsDraggingField(false)

        // Prevent null destination.
        if (!result.destination) return

        if (result.type === `group`) {
            const newCustomInputGroupOrder = Array.from(customInputGroupOrder)
            newCustomInputGroupOrder.splice(result.source.index, 1)
            newCustomInputGroupOrder.splice(result.destination.index, 0, result.draggableId)
            setCustomInputGroupOrder(newCustomInputGroupOrder)
        } else if (result.type === `field`) {

            // If the user drops the element in the same place.
            if (
                result.source.droppableId === result.destination.droppableId &&
                result.source.index === result.destination.index
            ) {
                return
            }

            const sourceGroup = result.source.droppableId
            const destinationGroup = result.destination.droppableId

            const sourceKey = groups[sourceGroup].customInputOrder[result.source.index]
            //const destinationKey = groups[destinationGroup].customInputOrder[result.destination.index]

            // Check if destination group has a custom field of the same name.
            if (
                result.source.droppableId !== result.destination.droppableId &&
                groups[destinationGroup].customInputOrder.includes(sourceKey)
            ) {
                return
            }

            // Copy old group and copy the dragged product.
            let newGroups = { ...groups }
            const targetCustomInput = newGroups[sourceGroup].customInputs[sourceKey]

            // Remove the dragged product from the old group.
            const sourceGroupCustomInputs = {} as Record<string, CustomInputAttributes>
            Object.keys(newGroups[sourceGroup].customInputs).forEach((key) => {
                if (key !== sourceKey) {
                    sourceGroupCustomInputs[key] = newGroups[sourceGroup].customInputs[key]
                }
            })
            newGroups[sourceGroup] = {
                ...newGroups[sourceGroup],
                customInputs: sourceGroupCustomInputs,
                customInputOrder: newGroups[sourceGroup].customInputOrder.filter((id: string) => id !== sourceKey),
            }

            // Add the dragged product to the new group.
            newGroups[destinationGroup] = {
                ...newGroups[destinationGroup],
                customInputOrder: [
                    ...newGroups[destinationGroup].customInputOrder.slice(0, result.destination.index),
                    sourceKey,
                    ...newGroups[destinationGroup].customInputOrder.slice(result.destination.index)
                ],
                customInputs: {
                    ...newGroups[destinationGroup].customInputs,
                    [sourceKey]: targetCustomInput
                }
            }

            // Set the new groups.
            setGroups(newGroups)
        }
    }

    // Handle add custom field group click.
    const handleAddFieldGroupClick = () => {
        openDialog(`add_custom_field_group`, { groups, setGroups, setCustomInputGroupOrder })
    }

    // Handle submit.
    const handleSubmit = async () => {
        setErrors([] as string[])
        
        try {
            // Remove groups missing from the order or vice versa.
            // This is done to fix database issues (a clean up).
            const filteredGroups: CustomInputGroups = {}
            customInputGroupOrder.forEach((groupName) => {
                if (groups[groupName]) {
                    filteredGroups[groupName] = groups[groupName]
                }
            })
            const filteredGroupOrder = customInputGroupOrder.filter((groupName) => {
                return Object.keys(filteredGroups).includes(groupName)
            })
            // Assemble data as json.
            const data = {
                ids: {
                    storageId: storageId,
                    categoryId: categoryId,
                },
                customInputs: {
                    customInputGroups: filteredGroups,
                    customInputGroupOrder: filteredGroupOrder
                }
            } as CustomInputWrite
            // Dispatch call using the mutation
            await setCustomInputsAsync(data)
            addToast({ text: t('toastCustomInputsUpdated'), colorScheme: 'green' })
        } catch (e: unknown) {
            setErrors([String(e instanceof Error ? e.message : e)])
            addToast({ text: t('toastCustomInputsError'), colorScheme: 'red' })
        }
    }

    // Handle errors.
    if (categoriesStatus === `loading`) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingStorage')}</Callout>

    // Return the JSX.
    return (
        <>
            <Form
                isLoading={isSettingCustomInputs}
                onSubmit={(e) => {
                    e.preventDefault()
                }}
            >
                <Section>
                    <div className={`drag_and_drop_area ${isDraggingGroup ? `active` : ``}`}>
                        {customInputGroupOrder.length > 0 && Object.keys(groups).length > 0 &&
                                <DragDropContext onDragEnd={isSettingCustomInputs ? () => {} : handleDragEnd} onDragStart={isSettingCustomInputs ? () => {} : handleDragStart}>
                                    <Droppable droppableId={`all-groups`} type="group" isDropDisabled={isSettingCustomInputs}>
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {customInputGroupOrder.map((groupName, key) => {
                                                    if (!groups[groupName]) {
                                                        logger.warn(`Group ${groupName} not found in groups, thus will be ignored`, groups)
                                                        return null
                                                    }
                                                    return <EditCustomInputsFormGroup
                                                                groups={groups}
                                                                setGroups={setGroups}
                                                                setCustomInputGroupOrder={setCustomInputGroupOrder}
                                                                groupName={groupName}
                                                                index={key}
                                                                key={groupName}
                                                                isDraggingGroup={isDraggingGroup}
                                                                isDraggingField={isDraggingField}
                                                                isSubmitting={isSettingCustomInputs} />
                                                })}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                        }
                        <div className={`mt-2 mb-4`}>
                            <Button wideOnMobile type="button" design="white" onClick={isSettingCustomInputs ? undefined : () => handleAddFieldGroupClick()} disabled={isSettingCustomInputs}><AddIcon /><span>{t('buttonAddGroup')}</span></Button>
                        </div>
                    </div>  

                    <div className={`input-holder`}>
                        {isSettingCustomInputs ? (
                            <ProcessingButton design="primary" />
                        ) : (
                            <Button wideOnMobile type="submit" design="primary" onClick={handleSubmit}><SaveIcon /><span>{t('buttonSave')}</span></Button>
                        )}
                    </div>
                    {errors.length > 0 && <FormErrors errors={errors} />}
                </Section>
            </Form>
        </>
    )

}

export default EditCustomInputsForm



